import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CALL_DIRECTION_LABELS,
  CALL_OUTCOME_LABELS,
  formatCallDuration,
  type Call,
  type CallOutcome,
  type CallRequest,
} from "@shared/modules/x-appels";
import { ApiError } from "../../../core/lib/api";
import { formatDateTime } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Select, Textarea } from "../../../core/components/ui/input";
import { BackLink } from "../../../core/components/ui/back-link";
import { CallBadge } from "../components/badges";
import { PhoneLinks, prettyPhone } from "../components/phone-links";
import { RequestCard } from "../components/request-card";
import { Transcript } from "../components/transcript";
import {
  atelierEnabled,
  getCall,
  listRequests,
  resendRequestSms,
  updateCall,
  updateRequest,
} from "../lib/api";
import { useElapsed } from "../lib/live";

type Failure = "not_found" | "error" | null;

function LiveTimer({ startedAt }: { startedAt: string | number }) {
  const elapsed = useElapsed(startedAt);
  return <>{formatCallDuration(elapsed)}</>;
}

/**
 * Fiche d'un appel : qui, quand, combien de temps, ce que l'assistante a
 * compris (résumé, véhicule, service, créneau demandé), la transcription
 * complète et ce que l'équipe en fait (traité, notes, suite donnée).
 * Un appel en cours se rafraîchit toutes les 2,5 s.
 */
export function AppelPage() {
  const { id } = useParams();
  const push = useToastStore((s) => s.push);
  const [call, setCall] = useState<Call | null>(null);
  const [failure, setFailure] = useState<Failure>(null);
  const [busy, setBusy] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  // Demandes (rappel, annulation) que Léo a prises pendant cet appel.
  const [requests, setRequests] = useState<CallRequest[]>([]);

  const load = useCallback(() => {
    if (!id) return;
    listRequests({ callId: id })
      .then(setRequests)
      .catch(() => setRequests([]));
    getCall(id)
      .then((c) => {
        setCall(c);
        setFailure(null);
      })
      .catch((err: unknown) => {
        setCall(null);
        setFailure(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
      });
  }, [id]);

  useEffect(load, [load]);

  // Tant que l'appel est en cours, la transcription arrive : on suit.
  useEffect(() => {
    if (call?.status !== "en_cours") return;
    const timer = setInterval(load, 2500);
    return () => clearInterval(timer);
  }, [call?.status, load]);

  const handleRequest = async (r: CallRequest) => {
    try {
      const next = await updateRequest(r.id, { status: "traitee" });
      setRequests((list) => list.map((x) => (x.id === next.id ? next : x)));
      push("success", "Demande traitée");
    } catch {
      push("danger", "Mise à jour impossible");
    }
  };
  const resendRequest = async (r: CallRequest) => {
    try {
      const next = await resendRequestSms(r.id);
      setRequests((list) => list.map((x) => (x.id === next.id ? next : x)));
      push(next.smsStatus === "echec" ? "danger" : "success", "Texto renvoyé");
    } catch {
      push("danger", "Renvoi impossible");
    }
  };

  const patch = async (input: Parameters<typeof updateCall>[1], message: string) => {
    if (!id || busy) return;
    setBusy(true);
    try {
      setCall(await updateCall(id, input));
      push("success", message);
    } catch {
      push("danger", "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  if (failure) {
    return (
      <div className="page">
        <p className="atelier-backlink">
          <BackLink fallback="/appels" />
        </p>
        <p className="empty">
          {failure === "not_found"
            ? "Appel introuvable."
            : "Fiche indisponible — réessayez dans un instant."}
        </p>
      </div>
    );
  }
  if (!call) return <p className="empty">Chargement…</p>;

  const live = call.status === "en_cours";
  const todo =
    !call.handled &&
    (call.outcome === "message" || call.outcome === "rappel" || call.status === "manque");

  return (
    <div className="page appel">
      <p className="atelier-backlink">
        <BackLink fallback="/appels" />
      </p>

      <header className={`appel__hero${live ? " appel__hero--live" : ""}`}>
        <div className="appel__hero-main">
          <p className="appel__eyebrow">
            <CallBadge status={call.status} outcome={call.outcome} />
            <span className="appel__direction">{CALL_DIRECTION_LABELS[call.direction]}</span>
            {call.handled ? <span className="appel__handled">Traité</span> : null}
          </p>
          <h1 className="appel__title">
            <Icon name="phone" size={26} />
            {call.callerName ?? prettyPhone(call.fromNumber)}
          </h1>
          <p className="appel__facts">
            {call.callerName ? <span>{prettyPhone(call.fromNumber)}</span> : null}
            <span>{formatDateTime(call.startedAt, { dateStyle: "full", timeStyle: "short" })}</span>
            <span>
              <Icon name="clock" size={14} />{" "}
              {live ? (
                <LiveTimer startedAt={call.startedAt} />
              ) : (
                formatCallDuration(call.durationSec)
              )}
            </span>
          </p>
          {call.intent ? <p className="appel__intent">{call.intent}</p> : null}
        </div>
        <div className="appel__hero-side">
          <PhoneLinks number={call.fromNumber} />
          {atelierEnabled && call.clientId ? (
            <Link to={`/clients/${call.clientId}`} className="btn btn--ghost">
              <span className="btn__icon">
                <Icon name="user" size={16} />
              </span>
              Fiche client
            </Link>
          ) : null}
          {atelierEnabled && call.appointmentId ? (
            <Link to={`/travaux/${call.appointmentId}`} className="btn btn--ghost">
              <span className="btn__icon">
                <Icon name="calendar" size={16} />
              </span>
              Voir le rendez-vous
            </Link>
          ) : null}
        </div>
      </header>

      {requests.length > 0 ? (
        <div className="appels-requests__grid">
          {requests.map((r) => (
            <RequestCard
              key={r.id}
              request={r}
              compact
              onHandled={(x) => void handleRequest(x)}
              onResend={(x) => void resendRequest(x)}
            />
          ))}
        </div>
      ) : null}

      {todo && !requests.some((r) => r.status === "a_traiter") ? (
        <div className="appel__todo" role="status">
          <Icon name="alert-circle" size={20} />
          <span>
            {call.status === "manque"
              ? "Appel manqué : rappelez ce numéro."
              : call.outcome === "rappel"
                ? "L'appelant attend un rappel de l'équipe."
                : "Un message a été pris : à rappeler dès que possible."}
          </span>
          <Button
            variant="success"
            size="sm"
            disabled={busy}
            icon={<Icon name="check" size={14} />}
            onClick={() => void patch({ handled: true }, "Appel traité")}
          >
            Marquer comme traité
          </Button>
        </div>
      ) : null}

      <div className="appel__grid">
        <section className="card appel__block">
          <h2 className="appel__block-title">Ce que l'assistante a compris</h2>
          {call.summary ? (
            <p className="appel__summary">{call.summary}</p>
          ) : (
            <p className="appel__empty">
              {live ? "Résumé disponible à la fin de l'appel." : "Aucun résumé."}
            </p>
          )}
          <dl className="kv">
            <dt>Véhicule</dt>
            <dd>{call.vehicle ?? "—"}</dd>
            <dt>Service</dt>
            <dd>{call.service ?? "—"}</dd>
            <dt>Créneau demandé</dt>
            <dd>{call.requestedAt ?? "—"}</dd>
            {call.recordingUrl ? (
              <>
                <dt>Enregistrement</dt>
                <dd>
                  <audio className="appel__audio" controls src={call.recordingUrl} preload="none" />
                </dd>
              </>
            ) : null}
          </dl>
        </section>

        <section className="card appel__block">
          <div className="appel__block-head">
            <h2 className="appel__block-title">Suite donnée</h2>
            {call.handled ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void patch({ handled: false }, "Remis à traiter")}
              >
                Remettre à traiter
              </Button>
            ) : (
              <Button
                variant="success"
                size="sm"
                disabled={busy || live}
                icon={<Icon name="check" size={14} />}
                onClick={() => void patch({ handled: true }, "Appel traité")}
              >
                Traité
              </Button>
            )}
          </div>
          <div className="appel__outcome">
            <label className="field__label" htmlFor="appel-outcome">
              Résultat
            </label>
            <Select
              id="appel-outcome"
              value={call.outcome}
              disabled={busy || live}
              onChange={(e) =>
                void patch({ outcome: e.target.value as CallOutcome }, "Résultat mis à jour")
              }
            >
              {(Object.keys(CALL_OUTCOME_LABELS) as CallOutcome[]).map((o) => (
                <option key={o} value={o}>
                  {CALL_OUTCOME_LABELS[o]}
                </option>
              ))}
            </Select>
          </div>
          <div className="appel__block-head">
            <h3 className="appel__block-subtitle">Notes de l'équipe</h3>
            {editingNotes ? (
              <span className="appel__block-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => setEditingNotes(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={async () => {
                    await patch({ notes: notes.trim() || null }, "Notes enregistrées");
                    setEditingNotes(false);
                  }}
                >
                  Enregistrer
                </Button>
              </span>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNotes(call.notes ?? "");
                  setEditingNotes(true);
                }}
              >
                Modifier
              </Button>
            )}
          </div>
          {editingNotes ? (
            <Textarea
              aria-label="Notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Rappelé le… / RDV déplacé… / Devis envoyé…"
              autoFocus
            />
          ) : call.notes ? (
            <p className="appel__notes">{call.notes}</p>
          ) : (
            <p className="appel__empty">Aucune note.</p>
          )}
        </section>

        <section className="card appel__block appel__block--wide">
          <div className="appel__block-head">
            <h2 className="appel__block-title">Transcription</h2>
            {live ? <span className="appel__live-hint">Mise à jour en direct</span> : null}
          </div>
          <Transcript lines={call.transcript} live={live} />
        </section>
      </div>
    </div>
  );
}
